import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileWarning, X } from 'lucide-react';
import { useCanAccess } from '@/hooks/useCanAccess';
import { useClinicSettings } from '@/hooks/useClinicSettings';
import { useDocumentTemplates } from '@/hooks/useDocumentTemplates';
import { faltaParaEmitirLabel } from '@/v2/lib/documentReadiness';

/**
 * RC-040 — o último lugar onde a falha ainda podia passar despercebida.
 *
 * A clínica liga o contrato, nunca abre a tela de Modelos de novo, e cada venda passa
 * sem gerar documento. Os avisos que existem hoje dependem de alguém estar olhando o
 * lugar certo: o toast some, e a tela de Modelos pode não ser visitada por semanas.
 *
 * Duas diferenças deliberadas em relação ao aviso de datas (`DateFixNoticeBanner`),
 * que é estático e some para sempre:
 *
 *  - **Aparece só quando o problema existe de verdade.** É derivado do estado (modelo
 *    ligado + dado faltando), não de uma data de release. Preencher o dado apaga o
 *    aviso sozinho, sem ninguém dispensar nada.
 *  - **Só para quem consegue resolver.** Quem não administra a clínica não tem a tela
 *    de dados — avisar seria cobrar uma ação impossível.
 *
 * O dispensar vale pela sessão, não para sempre: enquanto contrato nenhum estiver
 * sendo emitido, a clínica precisa reencontrar isso amanhã.
 */

export const DOCUMENT_SETUP_NOTICE_KEY = 'valiosas.documentSetupNotice.dismissed';

export function DocumentSetupNoticeBanner() {
  const navigate = useNavigate();
  const podeResolver = useCanAccess('settings:manage');
  const { data: templates } = useDocumentTemplates({ enabled: podeResolver });
  const { data: clinica } = useClinicSettings({ enabled: podeResolver });
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DOCUMENT_SETUP_NOTICE_KEY) === '1',
  );

  if (!podeResolver || dismissed) return null;

  // Este componente mora no SHELL: qualquer exceção aqui derruba a aplicação inteira,
  // não só o aviso. Payload fora do formato (rota com erro, resposta de proxy) vira
  // "nada a avisar", nunca tela branca.
  const bloqueados = (Array.isArray(templates) ? templates : [])
    .filter((t) => t?.isActive)
    .map((t) => ({
      nome: t.name,
      falta: faltaParaEmitirLabel(t.variables, {
        nome: clinica?.clinicName,
        cnpj: clinica?.clinicCnpj,
      }),
    }))
    .filter((t): t is { nome: string; falta: string } => Boolean(t.falta));

  if (bloqueados.length === 0) return null;

  // Uma clínica costuma ter um modelo travado, não cinco: nomear o documento é mais
  // útil que dizer "1 modelo". Com mais de um, o resumo evita um banner-parágrafo.
  const alvo = bloqueados[0];
  const titulo =
    bloqueados.length === 1
      ? `“${alvo.nome}” está ligado, mas nenhuma paciente está recebendo.`
      : `${bloqueados.length} documentos estão ligados, mas nenhuma paciente está recebendo.`;

  const dismiss = () => {
    sessionStorage.setItem(DOCUMENT_SETUP_NOTICE_KEY, '1');
    setDismissed(true);
  };

  return (
    <div className="flex flex-wrap items-start gap-3 border-b border-amber-200 bg-amber-50 px-4 py-3 text-so-14 text-amber-950 lg:px-8">
      <FileWarning className="mt-0.5 size-4 shrink-0 text-amber-700" />
      <div className="min-w-0 flex-1">
        <div className="font-bold">{titulo}</div>
        <p className="mt-1 text-amber-900">
          Para o sistema conseguir gerar o documento {alvo.falta}. Enquanto isso, cada
          agendamento e cada venda de pacote acontece normalmente, só que sem o documento.
        </p>
        <div className="mt-2">
          <button
            type="button"
            onClick={() => {
              dismiss();
              navigate('/config/clinica');
            }}
            // min-h-11 = 44px: no celular este é um alvo de dedo, não de mouse.
            className="min-h-11 rounded-so-sm bg-amber-900 px-3 py-1.5 text-so-12 font-bold text-white hover:bg-amber-800 sm:min-h-0"
          >
            Preencher agora
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Recolher aviso"
        className="inline-flex min-h-11 shrink-0 items-center gap-1 rounded-so-sm px-2 text-so-12 font-bold text-amber-700 hover:bg-amber-100"
      >
        <X className="size-4" />
        Recolher
      </button>
    </div>
  );
}
